import { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  FolderKanban,
  ClipboardList,
  GanttChart,
  MessageSquareMore,
  Settings,
  UserCog,
  HelpCircle,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  X,
  KeyRound,
  ChevronDown,
  FileSpreadsheet,
  BarChart3,
  PlayCircle,
  BookOpen,
  Bug,
  ScrollText,
  ClipboardCheck,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useTourStore } from '@/stores/tour-store';
import { Modal } from '@/components/ui/Modal';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ROLE_LABELS, type RoleType } from '@hancomum/shared';
import NotificationPopover from '@/components/NotificationPopover';
import NoticeIndicator from '@/components/NoticeIndicator';
import DevRoleSwitcher from '@/components/DevRoleSwitcher';
import { useReportAccess } from '@/features/storyboard-report/hooks';

interface NavItem {
  label: string;
  icon: React.ElementType;
  path: string;
  roles?: string[];
  dividerBefore?: boolean;
  badge?: string;
  badgeColor?: 'amber' | 'blue';
  highlight?: boolean;
}

const navItems: NavItem[] = [
  { label: '대시보드', icon: LayoutDashboard, path: '/dashboard' },
  { label: '프로젝트', icon: FolderKanban, path: '/projects' },
  { label: '개인업무', icon: ClipboardList, path: '/my-tasks' },
  { label: '간트차트', icon: GanttChart, path: '/gantt' },
  { label: '스토리보드', icon: FileSpreadsheet, path: '/storyboard', badge: '신규', badgeColor: 'blue' },
  { label: '스토리보드 집계', icon: BarChart3, path: '/storyboard/report', badge: '신규', badgeColor: 'blue' },
  { label: '회의록', icon: MessageSquareMore, dividerBefore: true, path: '/agendas' },
  { label: '내 설정', icon: UserCog, dividerBefore: true, path: '/settings' },
  { label: '시스템 설정', icon: Settings, path: '/admin', roles: ['master', 'admin'] },
  { label: '프로젝트 등록 요청', icon: ClipboardCheck, dividerBefore: true, path: '/project-requests', badge: '신규', badgeColor: 'blue' },
  { label: '버그/개선 접수', icon: Bug, path: '/issue-reports' },
  // 공지사항: 상단 헤더 NoticeIndicator(미읽음 배지)로 진입 — 사이드바 중복 제거
  // 패치노트: 자주 보지 않으므로 헤더 도움말(?) 드롭다운으로 이동
];

function useCurrentTime() {
  const [time, setTime] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return time.toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function Clock() {
  const currentTime = useCurrentTime();
  return (
    <div className="hidden sm:flex items-center px-2.5 py-1 rounded-md border border-gray-200 text-xs text-gray-500 font-mono tabular-nums">
      {currentTime}
    </div>
  );
}

function useUnreadCount() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const { data: count = 0, refetch } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const res = await api.get('/notifications/unread-count');
      return res.data.data?.count ?? 0;
    },
    enabled: isAuthenticated,
    refetchInterval: 30_000,
  });

  return { count, refetch };
}

// 내 담당 가등록 승인 대기 카운트 — 대시보드 메뉴 배지 (처리 전까지 사라지지 않는 지속 신호)
// queryKey가 ['dashboard','provisional'] prefix라 대시보드 승인/거절 시 함께 invalidate됨
function useMyProvisionalCount() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);

  const { data: count = 0 } = useQuery({
    queryKey: ['dashboard', 'provisional', 'my-count'],
    queryFn: async () => {
      const res = await api.get('/dashboard/provisional-count');
      return res.data.data?.count ?? 0;
    },
    enabled: isAuthenticated && role !== 'pending',
    refetchInterval: 30_000,
  });

  return count;
}

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const { count: unreadCount, refetch: refetchUnread } = useUnreadCount();
  const provisionalCount = useMyProvisionalCount();
  const { data: reportAccess } = useReportAccess();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const helpMenuRef = useRef<HTMLDivElement>(null);
  const tourStart = useTourStore((s) => s.startTour);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
    else setSidebarOpen(true);
  }, [isMobile]);

  // Close menus on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
      if (helpMenuRef.current && !helpMenuRef.current.contains(e.target as Node)) {
        setHelpMenuOpen(false);
      }
    }
    if (userMenuOpen || helpMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen, helpMenuOpen]);

  async function handleLogout() {
    setUserMenuOpen(false);
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    }
    clearAuth();
    queryClient.clear();
    toast.info('로그아웃되었습니다');
    navigate('/', { replace: true });

    try {
      const bc = new BroadcastChannel('hancomum-auth');
      bc.postMessage({ type: 'LOGOUT' });
      bc.close();
    } catch {
      // BroadcastChannel not supported
    }
  }

  const visibleNavItems = navItems.filter((item) => {
    // 스토리보드 집계는 런타임 config(min_role)로 노출 결정 (보안 경계는 서버)
    if (item.path === '/storyboard/report') return Boolean(reportAccess?.can_access);
    if (!item.roles) return true;
    return user?.role && item.roles.includes(user.role);
  });

  const handleNavClick = () => {
    if (isMobile) setSidebarOpen(false);
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Backdrop overlay (mobile only) */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        data-tour="layout-sidebar"
        className={cn(
          'fixed top-0 left-0 h-full bg-white border-r border-gray-200 z-40 flex flex-col overflow-hidden',
          isMobile
            ? cn('w-56 transition-transform duration-200', sidebarOpen ? 'translate-x-0' : '-translate-x-full')
            : cn('transition-all duration-200', sidebarOpen ? 'w-56' : 'w-16'),
        )}
      >
        {/* Sidebar header: UBIFLOW + toggle */}
        <div className="h-14 relative flex items-center border-b border-gray-200 shrink-0">
          {(sidebarOpen || isMobile) && (
            <div className="absolute inset-0 flex items-center pl-6 pointer-events-none">
              <span className="text-xl tracking-[0.25em] font-semibold bg-gradient-to-r from-blue-600 to-indigo-500 bg-clip-text text-transparent dark:from-blue-400 dark:to-indigo-300 select-none">
                UBIFLOW
              </span>
            </div>
          )}
          {/* Toggle button — 오른쪽 끝 고정 */}
          <button
            onClick={() => setSidebarOpen((prev) => !prev)}
            className={cn(
              'relative z-10 p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors',
              (sidebarOpen || isMobile) ? 'ml-auto mr-2' : 'mx-auto',
            )}
            title={isMobile && sidebarOpen ? '닫기' : sidebarOpen ? '사이드바 접기' : '사이드바 펼치기'}
          >
            {isMobile && sidebarOpen ? (
              <X size={18} />
            ) : sidebarOpen ? (
              <PanelLeftClose size={18} />
            ) : (
              <PanelLeftOpen size={18} />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {visibleNavItems.map((item) => {
            // 경로 경계까지 검사해 형제 라우트 오매칭 방지 (/storyboard 가 /storyboard/report 를 잡지 않도록).
            // 매칭되는 메뉴 중 '가장 구체적인(path 길이가 가장 긴)' 것만 active 처리 →
            // /projects/:id 같은 전용 메뉴 없는 상세는 부모(/projects)가 계속 강조됨.
            const matchesPath = (p: string) =>
              location.pathname === p || location.pathname.startsWith(p + '/');
            const isActive =
              matchesPath(item.path) &&
              !visibleNavItems.some(
                (other) =>
                  other.path !== item.path &&
                  other.path.length > item.path.length &&
                  matchesPath(other.path),
              );
            const Icon = item.icon;
            const approvalCount = item.path === '/dashboard' ? provisionalCount : 0;

            return (
              <div key={item.path}>
                {item.dividerBefore && (
                  <div className={cn('my-3', (sidebarOpen || isMobile) ? 'mx-2 border-t border-gray-200' : 'mx-3 border-t border-gray-200')} />
                )}
                <Link
                  to={item.path}
                  title={item.badge ? `${item.label} (${item.badge})` : item.label}
                  onClick={handleNavClick}
                  className={cn(
                    'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap',
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                  )}
                >
                  <span className="relative shrink-0">
                    <Icon size={20} className="shrink-0" />
                    {/* 접힌 사이드바에서도 승인 대기 신호 유지 (red dot) */}
                    {approvalCount > 0 && !(sidebarOpen || isMobile) && (
                      <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500" />
                    )}
                  </span>
                  {(sidebarOpen || isMobile) && (
                    <>
                      <span className={cn('flex-1 truncate', item.highlight && 'text-red-600 dark:text-red-400 font-bold')}>{item.label}</span>
                      {approvalCount > 0 && (
                        <span
                          className="ml-auto shrink-0 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white min-w-[18px] text-center"
                          title={`내 담당 승인 대기 ${approvalCount}건`}
                        >
                          {approvalCount > 99 ? '99+' : approvalCount}
                        </span>
                      )}
                      {item.badge && (
                        <span className={cn(
                          'ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                          item.badgeColor === 'blue'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
                        )}>
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main wrapper */}
      <div
        className={cn(
          'flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden transition-all duration-200',
          isMobile ? 'ml-0' : sidebarOpen ? 'ml-56' : 'ml-16',
        )}
      >
        {/* Header */}
        <header className="sticky top-0 z-20 bg-white border-b border-gray-200 h-14 shrink-0">
          <div className="h-full px-4 flex items-center justify-between">
            {/* Left - Hamburger (mobile) + Logo */}
            <div className="flex items-center gap-3">
              {isMobile && (
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="p-2 -ml-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                  title="메뉴"
                >
                  <Menu size={20} />
                </button>
              )}
              <h1 className="text-sm font-medium text-gray-500 tracking-tight">UBIFLOW</h1>
            </div>

            {/* Right - Notifications, Clock, User dropdown */}
            <div className="flex items-center gap-2">
              {/* Notice indicator */}
              <NoticeIndicator />

              {/* Notification popover */}
              <div data-tour="layout-notifications">
                <NotificationPopover unreadCount={unreadCount} onCountChange={refetchUnread} />
              </div>

              {/* Help guide */}
              <div className="relative" ref={helpMenuRef} data-tour="layout-help">
                <button
                  onClick={() => setHelpMenuOpen((prev) => !prev)}
                  className="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                  title="도움말"
                >
                  <HelpCircle size={20} />
                </button>

                {helpMenuOpen && (
                  <div className="absolute right-0 mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-lg py-1 z-50">
                    {tourStart && (
                      <button
                        onClick={() => {
                          setHelpMenuOpen(false);
                          tourStart();
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <PlayCircle size={16} className="text-gray-400" />
                        이 페이지 가이드
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setHelpMenuOpen(false);
                        navigate('/guide');
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <BookOpen size={16} className="text-gray-400" />
                      설치 가이드
                    </button>
                    <button
                      onClick={() => {
                        setHelpMenuOpen(false);
                        navigate('/patch-notes');
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <ScrollText size={16} className="text-gray-400" />
                      패치노트
                    </button>
                  </div>
                )}
              </div>

              {/* Dev role switcher (dev only) */}
              {import.meta.env.DEV && <DevRoleSwitcher />}

              {/* Clock */}
              <Clock />

              {/* User dropdown */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen((prev) => !prev)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors text-sm"
                >
                  <span className="font-medium text-gray-700">{user?.name}</span>
                  <span className="hidden sm:inline text-xs text-gray-400">
                    {ROLE_LABELS[(user?.role as RoleType) ?? 'user']}
                  </span>
                  <ChevronDown size={14} className={cn('text-gray-400 transition-transform', userMenuOpen && 'rotate-180')} />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-lg py-1 z-50">
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        setPwForm({ old_password: '', new_password: '', confirm_password: '' });
                        setPwModalOpen(true);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <KeyRound size={16} className="text-gray-400" />
                      비밀번호 변경
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut size={16} />
                      로그아웃
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Pending user banner */}
        {user?.role === 'pending' && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 text-sm text-amber-800">
            <strong>승인 대기 중:</strong> 관리자의 승인을 기다리고 있습니다. 승인 후 모든 기능을 사용할 수 있습니다.
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 flex flex-col p-3 md:p-6 min-w-0 min-h-0 overflow-auto [&>*]:w-full">
          <Outlet />
        </main>
      </div>

      {/* Password Change Modal */}
      <Modal
        isOpen={pwModalOpen}
        onClose={() => setPwModalOpen(false)}
        title="비밀번호 변경"
        size="sm"
        footer={
          <>
            <button
              type="button"
              onClick={() => setPwModalOpen(false)}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              취소
            </button>
            <button
              type="submit"
              form="pw-change-form"
              disabled={pwLoading}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {pwLoading ? '변경 중...' : '변경'}
            </button>
          </>
        }
      >
        <form
          id="pw-change-form"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!pwForm.old_password || !pwForm.new_password || !pwForm.confirm_password) {
              toast.error('모든 항목을 입력해주세요');
              return;
            }
            if (pwForm.new_password !== pwForm.confirm_password) {
              toast.error('새 비밀번호가 일치하지 않습니다');
              return;
            }
            if (pwForm.new_password.length < 6) {
              toast.error('새 비밀번호는 6자 이상이어야 합니다');
              return;
            }
            setPwLoading(true);
            try {
              await api.patch('/auth/password', {
                old_password: pwForm.old_password,
                new_password: pwForm.new_password,
              });
              toast.success('비밀번호가 변경되었습니다. 다시 로그인해주세요.');
              setPwModalOpen(false);
              clearAuth();
              queryClient.clear();
              navigate('/login');
            } catch (err: any) {
              toast.error(err.response?.data?.error?.message ?? '비밀번호 변경에 실패했습니다');
            } finally {
              setPwLoading(false);
            }
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">현재 비밀번호</label>
            <input
              type="password"
              value={pwForm.old_password}
              onChange={(e) => setPwForm((p) => ({ ...p, old_password: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">새 비밀번호</label>
            <input
              type="password"
              value={pwForm.new_password}
              onChange={(e) => setPwForm((p) => ({ ...p, new_password: e.target.value }))}
              className={cn(
                'w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1',
                pwForm.new_password && pwForm.new_password.length < 6
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
              )}
            />
            {pwForm.new_password && pwForm.new_password.length < 6 && (
              <p className="mt-1 text-xs text-red-500">6자 이상 입력해주세요</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">새 비밀번호 확인</label>
            <input
              type="password"
              value={pwForm.confirm_password}
              onChange={(e) => setPwForm((p) => ({ ...p, confirm_password: e.target.value }))}
              className={cn(
                'w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1',
                pwForm.confirm_password && pwForm.confirm_password !== pwForm.new_password
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
              )}
            />
            {pwForm.confirm_password && pwForm.confirm_password !== pwForm.new_password && (
              <p className="mt-1 text-xs text-red-500">비밀번호가 일치하지 않습니다</p>
            )}
          </div>
        </form>
      </Modal>
    </div>
  );
}
